/**
 * activityLog.ts — the Log Activity form → wire mapping, pure and tested.
 *
 * The Activities page used to append to local state and toast success over a
 * write that never happened. This is the half of the fix that can be proven
 * without a DOM: what the form's fields become on POST /v1/sales/activities.
 *
 * Rules, each one a data-honesty rule:
 *   - A field the row cannot hold is never dropped silently and never
 *     invented. `sales_activities` has no duration or outcome column, so
 *     what the rep picked is kept VERBATIM, labelled, in `body`, ahead of
 *     the rep's notes.
 *   - A deal is linked by id or not at all (slice A, RULED 2026-10-07): the
 *     sheet's deal is a PICKED open deal at the contact's account, sent as
 *     `dealId`. The old free-text deal, which rode in `body`, is gone.
 *   - A contact is linked by id or not at all: the form must hold the PICKED
 *     contact's id. A typed name that was never picked is refused.
 *   - `dueAt` is sent whenever the rep gave a date. A date with no time maps
 *     to LOCAL NOON, so the UTC calendar date the server compares
 *     (`due_at::date`) is the date the rep chose for any offset within ±11 h.
 *   - A task is never created already done: "Log Task" makes a PENDING task.
 *     Every other type logged with the primary button is completed after the
 *     create confirms.
 */
import type { SalesActivity } from '../../types'

export interface LogActivityForm {
  type: SalesActivity['type']
  description: string
  /** The PICKED contact's id — '' when the rep typed a name and picked nothing. */
  contactId: string
  date: string // 'YYYY-MM-DD' or ''
  time: string // 'HH:MM' or ''
  duration: string
  outcome: string
  /** The PICKED deal's id — '' for no deal. */
  dealId: string
  notes: string
}

export interface CreateActivityBody {
  activityType: string
  subject: string
  body?: string
  contactId?: string
  dealId?: string
  dueAt?: string
}

export type ActivityPayloadResult =
  | { ok: true; body: CreateActivityBody }
  | { ok: false; error: string }

/** ISO instant for the form's date (+ optional time), or undefined with no date. */
export function dueAtFrom(date: string, time: string): string | undefined {
  if (!date) return undefined
  const d = new Date(`${date}T${time || '12:00'}:00`)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/** The fields with no column, kept verbatim and labelled, then the notes. */
export function composeBody(form: Pick<LogActivityForm, 'outcome' | 'duration' | 'notes'>): string | undefined {
  const parts: string[] = []
  if (form.outcome.trim()) parts.push(`Outcome: ${form.outcome.trim()}`)
  if (form.duration.trim()) parts.push(`Duration: ${form.duration.trim()}`)
  const blocks = [parts.join(' · '), form.notes.trim()].filter(Boolean)
  return blocks.length ? blocks.join('\n\n') : undefined
}

export function buildActivityPayload(form: LogActivityForm): ActivityPayloadResult {
  const subject = form.description.trim()
  if (!subject) return { ok: false, error: 'Please enter a description' }
  if (!form.contactId) return { ok: false, error: 'Please select a contact' }
  const body = composeBody(form)
  const dueAt = dueAtFrom(form.date, form.time)
  return {
    ok: true,
    body: {
      activityType: form.type,
      subject,
      contactId: form.contactId,
      ...(form.dealId ? { dealId: form.dealId } : {}),
      ...(body ? { body } : {}),
      ...(dueAt ? { dueAt } : {}),
    },
  }
}

/** Whether the create is followed by a complete. A task never is. */
export function completesOnLog(type: SalesActivity['type'], requestedDone: boolean): boolean {
  return requestedDone && type !== 'task'
}
